// Drilldown 2.0 — facility → individual → spoke heatmaps modeled on the
// Tableau workbook of the same name.
//
// Clicking a row replaces the current table (does not stack below it).
// Back / Forward buttons walk the drill path the user just took.
//
// Scoped like the other analysis pages: sticky customer / saved filter /
// custom facility selection limits which facilities appear in the heatmap.
//
// v1 metrics (multi-select — each selected metric is its own heatmap row):
//   - Referrals Received (cached referrals by receiving_facility_id)
//   - Logins (unique users) (distinct user_id from the cached logins rollup)
//
// Other Tableau metrics appear in the Metrics list disabled.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCache } from '../../hooks/useCache';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useScopeSync } from '../../hooks/useScopeSync';
import { useCurrentScope } from '../../hooks/useCurrentScope';
import { useScope } from '../../contexts/ScopeContext';
import ScopeBar from '../../components/ScopeBar';
import FacilityFilter from '../../components/FacilityFilter';
import GroupFacilitiesModal from '../../components/GroupFacilitiesModal';
import { isTestRecord } from '../../utils/testDataFilter';
import {
  buildFacilityIndex,
  aggregateFacility,
  aggregateIndividual,
  aggregateSpoke,
  combineMetricHeatmaps,
  canonicalVeId,
  DRILLDOWN_LEVELS,
  GRANULARITIES,
  METRIC_OPTIONS,
} from '../../utils/drilldownAggregate';
import {
  downloadObjectsAsXlsx,
  heatmapToObjects,
  spokeResultToObjects,
} from '../../utils/exportTableauSheet';
import './Drilldown20.css';

const f = (r, k) => (r?.data || r?.Data || {})[k];

const ENABLED_METRIC_IDS = METRIC_OPTIONS.filter(m => m.enabled).map(m => m.id);

function metricMeta(id) {
  return METRIC_OPTIONS.find(m => m.id === id) || null;
}

const DATE_PRESETS = [
  { id: 'last_month',    label: 'Last 30 days' },
  { id: 'last_3_months', label: 'Last 3 months' },
  { id: 'last_6_months', label: 'Last 6 months' },
  { id: 'ytd',           label: 'Year to date' },
  { id: 'last_year',     label: 'Last 12 months' },
  { id: 'custom',        label: 'Custom range' },
];

const ROOT_STEP = {
  level: 'facility',
  facilitySfId: null,
  hubUserId: null,
  facilityLabel: null,
  userLabel: null,
};

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function rangeFromPreset(preset) {
  const end = new Date();
  const start = new Date();
  switch (preset) {
    case 'last_month':    start.setDate(start.getDate() - 30); break;
    case 'last_3_months': start.setMonth(start.getMonth() - 3); break;
    case 'last_6_months': start.setMonth(start.getMonth() - 6); break;
    case 'ytd':           start.setMonth(0); start.setDate(1); break;
    case 'last_year':     start.setFullYear(start.getFullYear() - 1); break;
    default: return { start: null, end: null };
  }
  return { start, end };
}

// Per-metric heatmap palettes — intensity still encodes magnitude, but
// hue separates Referrals (teal) from Logins (indigo) at a glance.
const METRIC_PALETTES = {
  'referrals-received': {
    rgb: '15, 118, 110',
    textDark: '#134e4a',
    chipBg: '#ccfbf1',
    chipFg: '#0f766e',
    chipBorder: '#5eead4',
  },
  'logins-unique-users': {
    rgb: '79, 70, 229',
    textDark: '#312e81',
    chipBg: '#e0e7ff',
    chipFg: '#4338ca',
    chipBorder: '#a5b4fc',
  },
};

const DEFAULT_PALETTE = METRIC_PALETTES['referrals-received'];

function paletteFor(metricId) {
  return METRIC_PALETTES[metricId] || DEFAULT_PALETTE;
}

function HeatmapCell({ value, rowMax, metricId }) {
  if (!value) return <td className="dd-cell dd-cell-empty">·</td>;
  const pct = rowMax > 0 ? Math.min(1, value / rowMax) : 0;
  const alpha = 0.15 + pct * 0.75;
  const palette = paletteFor(metricId);
  const style = {
    backgroundColor: `rgba(${palette.rgb}, ${alpha.toFixed(3)})`,
    color: pct > 0.55 ? '#f8fafc' : palette.textDark,
  };
  return <td className="dd-cell" style={style}>{value.toLocaleString()}</td>;
}

function HeatmapTable({
  columns,
  rows,
  onRowClick,
  isRowClickable,
  emptyMessage,
  expandedIds,
  onToggleExpand,
}) {
  const hierarchical = rows.some(r => r.parentSfId != null || r.hasChildren || (r.depth != null && r.depth > 0));
  const expanded = expandedIds || new Set();

  // Prefer entityKey (facility/user id) so multi-metric sibling rows share
  // one expand/collapse key. Fall back to rowKey for single-metric tables.
  const entityOf = (r) => String(r?.entityKey || r?.rowKey || '');

  const rowByEntity = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = entityOf(r);
      if (key && !m.has(key)) m.set(key, r);
    }
    return m;
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!rows.length) return [];
    if (!hierarchical || !onToggleExpand) return rows;
    return rows.filter((r) => {
      let parent = r.parentSfId;
      while (parent) {
        if (!expanded.has(String(parent))) return false;
        parent = rowByEntity.get(String(parent))?.parentSfId || null;
      }
      return true;
    });
  }, [rows, hierarchical, onToggleExpand, expanded, rowByEntity]);

  // When more than one metric is on the table, show a fixed chip for the
  // metric name so it never disappears behind ellipsis on long facility names.
  const showMetricChip = useMemo(() => {
    const ids = new Set();
    for (const r of rows) {
      if (r.metricId) ids.add(r.metricId);
      if (ids.size > 1) return true;
    }
    return false;
  }, [rows]);

  if (!columns.length || !rows.length || !visibleRows.length) {
    return <div className="dd-empty">{emptyMessage || 'No data for this window yet.'}</div>;
  }

  return (
    <div className="dd-table-scroll">
      <table className="dd-heatmap">
        <thead>
          <tr>
            <th className="dd-row-header">Row</th>
            {columns.map(c => <th key={c.key} className="dd-col-header">{c.label}</th>)}
            <th className="dd-col-header dd-total-header">Total</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(r => {
            const rowMax = Math.max(0, ...columns.map(c => r.buckets[c.key] || 0));
            const depth = Number(r.depth) || 0;
            const allowClick = typeof onRowClick === 'function'
              && !r.isGroup
              && (typeof isRowClickable !== 'function' || isRowClickable(r));
            const entityKey = entityOf(r);
            const canExpand = Boolean(r.hasChildren && onToggleExpand);
            const isExpanded = expanded.has(entityKey);
            const displayName = r.entityLabel || r.rowLabel;
            const chipPalette = showMetricChip && r.metricLabel ? paletteFor(r.metricId) : null;
            return (
              <tr
                key={r.rowKey}
                className={[
                  'dd-row',
                  allowClick ? 'dd-row-clickable' : '',
                  r.isGroup ? 'dd-row-group' : '',
                  canExpand && !allowClick ? 'dd-row-expandable' : '',
                ].filter(Boolean).join(' ')}
                onClick={allowClick ? () => onRowClick(r) : (canExpand && r.isGroup ? () => onToggleExpand(entityKey) : undefined)}
              >
                <td
                  className="dd-row-label"
                  title={r.rowLabel}
                  style={{ paddingLeft: `${0.5 + depth * 1.35}rem` }}
                >
                  {canExpand ? (
                    <button
                      type="button"
                      className="dd-expand-btn"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? `Collapse ${r.rowLabel}` : `Expand ${r.rowLabel}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleExpand(entityKey);
                      }}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>
                  ) : (
                    <span className="dd-expand-spacer" aria-hidden="true" />
                  )}
                  <span className="dd-row-label-text">{displayName}</span>
                  {chipPalette && (
                    <span
                      className="dd-metric-chip"
                      style={{
                        background: chipPalette.chipBg,
                        color: chipPalette.chipFg,
                        borderColor: chipPalette.chipBorder,
                      }}
                    >
                      {r.metricLabel}
                    </span>
                  )}
                  {allowClick && <span className="dd-row-drill-hint" aria-hidden="true">→</span>}
                </td>
                {columns.map(c => (
                  <HeatmapCell
                    key={c.key}
                    value={r.buckets[c.key] || 0}
                    rowMax={rowMax}
                    metricId={r.metricId}
                  />
                ))}
                <td className="dd-row-total">{r.total.toLocaleString()}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SpokePane({ result }) {
  if (!result) return null;
  if (!result.rows.length) {
    return <div className="dd-empty">No inbound referrals for this hub in this window.</div>;
  }
  return (
    <div className="dd-spoke">
      {result.rows.map(fac => (
        <details key={fac.rowKey} className="dd-spoke-facility">
          <summary>
            <span className="dd-spoke-name">{fac.rowLabel}</span>
            <span className="dd-spoke-count">{fac.total.toLocaleString()}</span>
          </summary>
          <table className="dd-spoke-user-table">
            <thead>
              <tr><th>Sending user</th><th className="dd-num">Referrals</th></tr>
            </thead>
            <tbody>
              {fac.users.map(u => (
                <tr key={u.rowKey}>
                  <td>{u.rowLabel}</td>
                  <td className="dd-num">{u.total.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      ))}
    </div>
  );
}

function Drilldown20() {
  const cache = useCache();
  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();
  const { clearScope: clearGlobalScope } = useScope();

  // Multi-select: each selected metric becomes a sibling heatmap row
  // ("Name · Referrals", "Name · Logins") under the same facility/user.
  const [metrics, setMetrics] = useState(() => ['referrals-received']);
  const [granularity, setGranularity] = useState('month');
  const [drilldown, setDrilldown] = useState('facility');
  const [dateRangePreset, setDateRangePreset] = useState('last_year');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [removeTestData, setRemoveTestData] = useState(true);

  const metricsKey = metrics.join(',');
  const includesReferrals = metrics.includes('referrals-received');
  const multiMetric = metrics.length > 1;

  const toggleMetric = useCallback((id) => {
    if (!ENABLED_METRIC_IDS.includes(id)) return;
    setMetrics((prev) => {
      if (prev.includes(id)) {
        // Keep at least one metric selected.
        if (prev.length <= 1) return prev;
        return prev.filter(m => m !== id);
      }
      // Preserve catalog order so heatmap sibling rows stay consistent.
      const next = [...prev, id];
      return ENABLED_METRIC_IDS.filter(m => next.includes(m));
    });
  }, []);

  const [selectedFacilityIds, setSelectedFacilityIds] = useState(() => new Set());
  const [activeCustomerId, setActiveCustomerId] = useState('');
  const [activeFilterId, setActiveFilterId] = useState('');
  const [savedFilters, setSavedFilters] = useState([]);
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  const [hierarchy, setHierarchy] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Drill navigation: one visible level at a time, with back/forward history.
  const [history, setHistory] = useState([ROOT_STEP]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const step = history[historyIndex] || ROOT_STEP;

  // Salesforce hierarchy expand/collapse on the facility heatmap.
  // Empty = all collapsed (only root-level rows visible).
  const [expandedSfIds, setExpandedSfIds] = useState(() => new Set());

  const resetHistory = useCallback(() => {
    setHistory([ROOT_STEP]);
    setHistoryIndex(0);
    setExpandedSfIds(new Set());
  }, []);

  const toggleExpanded = useCallback((sfId) => {
    const key = String(sfId);
    setExpandedSfIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const pushStep = useCallback((next) => {
    setHistory(prev => {
      const truncated = prev.slice(0, historyIndex + 1);
      return [...truncated, next];
    });
    setHistoryIndex(i => i + 1);
  }, [historyIndex]);

  const goBack = useCallback(() => {
    setHistoryIndex(i => Math.max(0, i - 1));
  }, []);

  const goForward = useCallback(() => {
    setHistoryIndex(i => Math.min(history.length - 1, i + 1));
  }, [history.length]);

  const canBack = historyIndex > 0;
  const canForward = historyIndex < history.length - 1;

  useEffect(() => {
    (async () => {
      try {
        const r = await cache.getCachedFacilityHierarchy();
        if (r?.success) setHierarchy(r.hierarchy || []);
      } catch { /* optional */ }
      try {
        const r = await cache.getSavedFilters();
        if (r?.filters) setSavedFilters(r.filters);
      } catch { /* no filters yet */ }
    })();
    const { start, end } = rangeFromPreset('last_year');
    setStartDate(ymd(start));
    setEndDate(ymd(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = rangeFromPreset(dateRangePreset);
      if (start && end) {
        setStartDate(ymd(start));
        setEndDate(ymd(end));
      }
    }
  }, [dateRangePreset]);

  // Changing the top-level drill dimension invalidates the facility/user path.
  useEffect(() => {
    resetHistory();
  }, [drilldown, metricsKey, granularity, resetHistory]);

  const index = useMemo(() => buildFacilityIndex(hierarchy), [hierarchy]);

  const load = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError('');
    setHasLoaded(false);
    resetHistory();

    const needsReferrals = metrics.includes('referrals-received');
    const needsLogins = metrics.includes('logins-unique-users');

    try {
      const tasks = [];
      if (needsReferrals) tasks.push(['Referrals', 'referrals']);
      if (needsLogins) tasks.push(['Logins', 'logins']);

      setProgress({ current: 0, total: Math.max(tasks.length, 1), label: 'Starting…' });
      for (let i = 0; i < tasks.length; i++) {
        const [label, type] = tasks[i];
        setProgress({ current: i, total: tasks.length, label });
        // eslint-disable-next-line no-await-in-loop
        const res = await cache.getCachedData(type, startDate, endDate);
        const rows = res?.success ? (res.records || []) : [];
        if (type === 'referrals') setReferrals(rows);
        else setLogins(rows);
      }
      // Clear the unused series so counts in the header stay honest.
      if (!needsReferrals) setReferrals([]);
      if (!needsLogins) setLogins([]);
      setProgress({ current: tasks.length, total: Math.max(tasks.length, 1), label: 'Done' });
      setHasLoaded(true);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Load failed.');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(null), 1200);
    }
  }, [cache, metrics, startDate, endDate, resetHistory]);

  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData: load,
    ready: Boolean(startDate && endDate),
  });

  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) setSelectedFacilityIds(new Set());
    else {
      const sf = savedFilters.find(s => String(s.id) === String(filterId));
      if (sf?.facilityIds) setSelectedFacilityIds(new Set(sf.facilityIds.map(String)));
    }
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) setSelectedFacilityIds(new Set());
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids.map(String)));
    }
    setScopeFromCustomer(customerId);
  };

  // Facility ids present in loaded data (drives the Custom Filter tree).
  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    for (const r of referrals) {
      const recv = canonicalVeId(f(r, 'receiving_facility_id'));
      const send = canonicalVeId(f(r, 'sending_facility_id'));
      if (recv) ids.add(recv);
      if (send) ids.add(send);
    }
    for (const r of logins) {
      const fac = canonicalVeId(f(r, 'facility_id'));
      if (fac) ids.add(fac);
    }
    return ids;
  }, [referrals, logins]);

  const filteredReferrals = useMemo(() => {
    let rows = referrals;
    if (removeTestData) rows = rows.filter(r => !isTestRecord(r));
    if (selectedFacilityIds.size > 0) {
      const facIds = new Set([...selectedFacilityIds].map(canonicalVeId));
      rows = rows.filter(r => {
        const recv = canonicalVeId(f(r, 'receiving_facility_id'));
        return recv && facIds.has(recv);
      });
    }
    return rows;
  }, [referrals, removeTestData, selectedFacilityIds]);

  const filteredLogins = useMemo(() => {
    let rows = logins;
    if (removeTestData) rows = rows.filter(r => !isTestRecord(r));
    if (selectedFacilityIds.size > 0) {
      const facIds = new Set([...selectedFacilityIds].map(canonicalVeId));
      rows = rows.filter(r => {
        const fac = canonicalVeId(f(r, 'facility_id'));
        return fac && facIds.has(fac);
      });
    }
    return rows;
  }, [logins, removeTestData, selectedFacilityIds]);

  // Scope changes should drop any in-progress drill path.
  const scopeKey = useMemo(
    () => [...selectedFacilityIds].sort().join(','),
    [selectedFacilityIds]
  );
  useEffect(() => {
    resetHistory();
  }, [scopeKey, resetHistory]);

  const facilityResult = useMemo(() => {
    if (!hasLoaded || metrics.length === 0) return { columns: [], rows: [] };
    const parts = metrics.map((metricId) => {
      const meta = metricMeta(metricId);
      const agg = aggregateFacility({
        metric: metricId,
        granularity,
        drilldown,
        referrals: filteredReferrals,
        logins: filteredLogins,
        index,
        startYmd: startDate,
        endYmd: endDate,
      });
      return {
        metricId,
        label: meta?.label || metricId,
        shortLabel: meta?.shortLabel || meta?.label || metricId,
        columns: agg.columns,
        rows: agg.rows,
      };
    });
    return combineMetricHeatmaps(parts);
  }, [hasLoaded, metrics, granularity, drilldown, filteredReferrals, filteredLogins, index, startDate, endDate]);

  const individualResult = useMemo(() => {
    if (!hasLoaded || !step.facilitySfId || metrics.length === 0) return null;
    const parts = metrics.map((metricId) => {
      const meta = metricMeta(metricId);
      const agg = aggregateIndividual({
        metric: metricId,
        granularity,
        facilitySfId: step.facilitySfId,
        referrals: filteredReferrals,
        logins: filteredLogins,
        index,
        startYmd: startDate,
        endYmd: endDate,
      });
      return {
        metricId,
        label: meta?.label || metricId,
        shortLabel: meta?.shortLabel || meta?.label || metricId,
        columns: agg.columns,
        rows: agg.rows,
      };
    });
    return combineMetricHeatmaps(parts);
  }, [hasLoaded, step.facilitySfId, metrics, granularity, filteredReferrals, filteredLogins, index, startDate, endDate]);

  const spokeResult = useMemo(() => {
    if (!hasLoaded || !step.facilitySfId) return null;
    if (!includesReferrals) return null;
    return aggregateSpoke({
      facilitySfId: step.facilitySfId,
      hubUserId: step.hubUserId,
      referrals: filteredReferrals,
      index,
    });
  }, [hasLoaded, step.facilitySfId, step.hubUserId, includesReferrals, filteredReferrals, index]);

  const handleFacilityRowClick = useCallback((row) => {
    const entityKey = String(row.entityKey || row.rowKey || '');
    const sfId = drilldown === 'facility'
      ? (entityKey.startsWith('ve:') ? null : entityKey)
      : (row.facilityIds?.[0] || null);
    if (!sfId) return;
    const node = index.bySfId(sfId);
    const facilityLabel = node
      ? (node.name || node.Name || row.entityLabel || row.rowLabel)
      : (row.entityLabel || row.rowLabel);
    pushStep({
      level: 'individual',
      facilitySfId: sfId,
      hubUserId: null,
      facilityLabel,
      userLabel: null,
    });
  }, [drilldown, index, pushStep]);

  const handleUserRowClick = useCallback((row) => {
    // Spoke view is only meaningful for the referrals-received metric row.
    if (row.metricId && row.metricId !== 'referrals-received') return;
    if (!includesReferrals) return;
    const hubUserId = String(row.entityKey || row.rowKey || '');
    if (!hubUserId || hubUserId.includes('::')) return;
    pushStep({
      level: 'spoke',
      facilitySfId: step.facilitySfId,
      hubUserId,
      facilityLabel: step.facilityLabel,
      userLabel: row.entityLabel || row.rowLabel,
    });
  }, [includesReferrals, pushStep, step.facilitySfId, step.facilityLabel]);

  const selectedMetricInfos = metrics.map(metricMeta).filter(Boolean);
  const metricsLabel = selectedMetricInfos.map(m => m.shortLabel || m.label).join(' + ')
    || 'Metric';
  const disabledMetrics = METRIC_OPTIONS.filter(m => !m.enabled);

  const breadcrumb = (() => {
    const parts = [metricsLabel];
    if (step.facilityLabel) parts.push(step.facilityLabel);
    if (step.level === 'spoke') {
      parts.push(step.userLabel || 'Spoke');
    } else if (step.level === 'individual') {
      parts.push('Individuals');
    }
    return parts;
  })();

  let viewTitle = `Facility heatmap · ${metricsLabel}`;
  let viewHint = drilldown === 'facility'
    ? 'Hierarchy starts collapsed. Expand a parent to see facilities underneath, or click a facility to drill into individuals.'
    : 'Click a row to drill into individuals at that facility.';
  if (multiMetric) {
    viewHint = `${viewHint} Each selected metric is its own row under the same facility.`;
  }
  if (step.level === 'individual') {
    viewTitle = `Individual view · ${step.facilityLabel || 'Selected facility'}`;
    viewHint = includesReferrals
      ? (multiMetric
        ? 'Each metric is its own row. Click a Referrals row to see inbound spokes for that hub.'
        : 'Click a user to see inbound spokes for that hub.')
      : 'End of drill path for this metric.';
  } else if (step.level === 'spoke') {
    viewTitle = `Spoke drilldown · ${step.userLabel || 'Hub user'}`;
    viewHint = `Inbound referrals to ${step.facilityLabel || 'facility'}${step.userLabel ? ` / ${step.userLabel}` : ''}.`;
  }

  const hierarchyExpandableIds = useMemo(() => {
    if (drilldown !== 'facility') return [];
    return facilityResult.rows
      .filter(r => r.hasChildren)
      .map(r => String(r.entityKey || r.rowKey));
  }, [drilldown, facilityResult.rows]);

  const expandAllHierarchy = useCallback(() => {
    setExpandedSfIds(new Set(hierarchyExpandableIds));
  }, [hierarchyExpandableIds]);

  const collapseAllHierarchy = useCallback(() => {
    setExpandedSfIds(new Set());
  }, []);

  const exportCurrentView = useCallback(() => {
    const dates = { startDate, endDate };
    if (step.level === 'spoke') {
      const objects = spokeResultToObjects(spokeResult, {
        facilityLabel: step.facilityLabel,
        userLabel: step.userLabel,
      });
      const spokeHeaders = [
        'Hub facility', 'Hub user', 'Sending facility', 'Sending user', 'Referrals',
      ];
      downloadObjectsAsXlsx(objects, {
        fileStem: 'drilldown20_spoke',
        sheetName: 'Spoke',
        headers: spokeHeaders,
        ...dates,
      });
      return;
    }
    const result = step.level === 'individual' ? individualResult : facilityResult;
    if (!result?.rows?.length) return;
    const { rows: exportRows, headers } = heatmapToObjects(result, {
      idColumns: [
        { key: 'entityLabel', header: 'Name' },
        { key: 'rowLabel', header: 'Row label' },
        { key: 'metricLabel', header: 'Metric' },
        { key: 'depth', header: 'Depth' },
      ],
    });
    downloadObjectsAsXlsx(exportRows, {
      fileStem: step.level === 'individual' ? 'drilldown20_individuals' : 'drilldown20_facilities',
      sheetName: step.level === 'individual' ? 'Individuals' : 'Facilities',
      headers,
      ...dates,
    });
  }, [
    startDate, endDate, step, spokeResult, individualResult, facilityResult,
  ]);

  const canExport = step.level === 'spoke'
    ? Boolean(spokeResult?.rows?.length)
    : step.level === 'individual'
      ? Boolean(individualResult?.rows?.length)
      : Boolean(facilityResult?.rows?.length);

  return (
    <div className="dd20">
      <p className="dd20-intro">
        Facility → Individual → Spoke drilldown, in the spirit of the Tableau
        <em> Drilldown 2.0</em> workbook. Click a row to replace this table with
        the next level; use Back / Forward to move through the path you took.
        Pick a customer, saved filter, or custom facility set to limit the heatmap.
      </p>

      <div className="dd20-controls">
        <div className="dd20-field dd20-field-metrics">
          <span className="dd20-field-label" id="dd20-metric-label">Metrics</span>
          <div className="dd20-metric-list" role="group" aria-labelledby="dd20-metric-label">
            {METRIC_OPTIONS.map(m => {
              const checked = metrics.includes(m.id);
              return (
                <label
                  key={m.id}
                  className={[
                    'dd20-metric-option',
                    m.enabled ? '' : 'dd20-metric-option-disabled',
                    checked ? 'dd20-metric-option-on' : '',
                  ].filter(Boolean).join(' ')}
                  title={m.note}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!m.enabled || loading}
                    onChange={() => toggleMetric(m.id)}
                  />
                  <span>
                    {m.label}{m.enabled ? '' : ' — needs API'}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="dd20-hint">
            {multiMetric
              ? 'Each selected metric is its own heatmap row under the same facility / user.'
              : (selectedMetricInfos[0]?.note || 'Select one or more metrics.')}
          </div>
        </div>

        <div className="dd20-field">
          <label htmlFor="dd20-period">Period</label>
          <select
            id="dd20-period"
            value={granularity}
            onChange={e => setGranularity(e.target.value)}
          >
            {GRANULARITIES.map(g => (
              <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="dd20-field">
          <label htmlFor="dd20-drill">Drilldown</label>
          <select
            id="dd20-drill"
            value={drilldown}
            onChange={e => setDrilldown(e.target.value)}
          >
            {DRILLDOWN_LEVELS.map(d => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </select>
        </div>

        <div className="dd20-field">
          <label htmlFor="dd20-date">Date range</label>
          <select
            id="dd20-date"
            value={dateRangePreset}
            onChange={e => setDateRangePreset(e.target.value)}
            disabled={loading}
          >
            {DATE_PRESETS.map(p => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>

        {dateRangePreset === 'custom' && (
          <>
            <div className="dd20-field">
              <label htmlFor="dd20-start">Start</label>
              <input
                id="dd20-start"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="dd20-field">
              <label htmlFor="dd20-end">End</label>
              <input
                id="dd20-end"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </>
        )}

        {savedFilters.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="dd20-saved-filter">Saved filter</label>
            <select
              id="dd20-saved-filter"
              value={activeFilterId}
              onChange={e => applySavedFilter(e.target.value)}
              disabled={loading}
            >
              <option value="">None (show all)</option>
              {savedFilters.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.facilityIds?.length || 0})
                </option>
              ))}
            </select>
          </div>
        )}

        {customers.length > 0 && (
          <div className="dd20-field">
            <label htmlFor="dd20-customer">Customer</label>
            <select
              id="dd20-customer"
              value={activeCustomerId}
              onChange={e => applyCustomerFilter(e.target.value)}
              disabled={loading}
            >
              <option value="">None (show all)</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c._depth > 0 ? '— ' : ''}{c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          type="button"
          className="dd20-filter-btn"
          onClick={() => setShowFacilityFilter(true)}
          disabled={loading}
        >
          Custom filter
          {selectedFacilityIds.size > 0 ? ` (${selectedFacilityIds.size})` : ''}
        </button>

        <label className="dd20-check">
          <input
            type="checkbox"
            checked={removeTestData}
            onChange={e => setRemoveTestData(e.target.checked)}
          />
          Remove test data
        </label>

        <button
          type="button"
          className="dd20-load"
          onClick={load}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? 'Loading…' : 'Load data'}
        </button>
      </div>

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && hierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={hierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {progress && (
        <div className="dd20-progress">{progress.label} ({progress.current}/{progress.total})</div>
      )}
      {error && <div className="dd20-error">{error}</div>}
      {!hasLoaded && !loading && (
        <div className="dd20-empty-first">
          Optionally pick a customer, saved filter, or custom facility set, then
          choose one or more metrics / period / date range and click
          <strong> Load data</strong>. Referrals Received and Logins work today;
          other metrics are grayed out until the underlying API is enabled.
        </div>
      )}

      {hasLoaded && (
        <>
          <div className="dd20-nav">
            <div className="dd20-nav-buttons">
              <button
                type="button"
                className="dd20-nav-btn"
                onClick={goBack}
                disabled={!canBack}
                title="Back to previous drill level"
              >
                ← Back
              </button>
              <button
                type="button"
                className="dd20-nav-btn"
                onClick={goForward}
                disabled={!canForward}
                title="Forward to next drill level"
              >
                Forward →
              </button>
              {step.level !== 'facility' && (
                <button
                  type="button"
                  className="dd20-nav-btn dd20-nav-btn-reset"
                  onClick={resetHistory}
                  title="Return to the facility heatmap"
                >
                  Top level
                </button>
              )}
            </div>
            <div className="dd20-breadcrumb" aria-label="Drill path">
              {breadcrumb.map((part, i) => (
                <React.Fragment key={`${part}-${i}`}>
                  {i > 0 && <span className="dd20-breadcrumb-sep">›</span>}
                  <span className={i === breadcrumb.length - 1 ? 'dd20-breadcrumb-current' : ''}>
                    {part}
                  </span>
                </React.Fragment>
              ))}
            </div>
            <span className="dd20-scope-tag">
              {filteredReferrals.length.toLocaleString()} referrals · {filteredLogins.length.toLocaleString()} login rollups
              {selectedFacilityIds.size > 0
                ? ` · ${selectedFacilityIds.size.toLocaleString()} facilit${selectedFacilityIds.size === 1 ? 'y' : 'ies'} in scope`
                : ''}
            </span>
          </div>

          <div className="dd20-section-header">
            <div>
              <h3>{viewTitle}</h3>
              <p className="dd20-view-hint">{viewHint}</p>
            </div>
            <div className="dd20-section-actions">
              {step.level === 'facility' && drilldown === 'facility' && hierarchyExpandableIds.length > 0 && (
                <>
                  <button type="button" className="dd20-nav-btn" onClick={expandAllHierarchy}>
                    Expand all
                  </button>
                  <button type="button" className="dd20-nav-btn" onClick={collapseAllHierarchy}>
                    Collapse all
                  </button>
                </>
              )}
              <button
                type="button"
                className="dd20-export-btn"
                onClick={exportCurrentView}
                disabled={!canExport}
                title="Download the current drill view as Excel"
              >
                Export Excel
              </button>
            </div>
          </div>

          {step.level === 'facility' && (
            <HeatmapTable
              columns={facilityResult.columns}
              rows={facilityResult.rows}
              onRowClick={handleFacilityRowClick}
              expandedIds={drilldown === 'facility' ? expandedSfIds : undefined}
              onToggleExpand={drilldown === 'facility' ? toggleExpanded : undefined}
              emptyMessage="No rows match this window. Try widening the date range, clearing the facility scope, or switching metric."
            />
          )}

          {step.level === 'individual' && individualResult && (
            <HeatmapTable
              columns={individualResult.columns}
              rows={individualResult.rows}
              onRowClick={includesReferrals ? handleUserRowClick : undefined}
              isRowClickable={includesReferrals
                ? (row) => !row.metricId || row.metricId === 'referrals-received'
                : undefined}
              emptyMessage="No users under this facility have activity in the current window."
            />
          )}

          {step.level === 'spoke' && (
            <SpokePane result={spokeResult} />
          )}
        </>
      )}

      <details className="dd20-gaps">
        <summary>Metrics not yet available (API gaps)</summary>
        <p>
          Send this ask list to the CTO to unlock full Tableau parity. Nothing
          on this list blocks Referrals Received or Logins from working today.
        </p>
        <ul>
          {disabledMetrics.map(m => (
            <li key={m.id}>
              <strong>{m.label}</strong> — {m.note}
            </li>
          ))}
          <li>
            <strong>User email &amp; user_type on referral rows</strong> — today
            <code> user_type</code> only lives on logins/messages; email is patchy via
            the users lookup. Both are needed to enrich the Individual View.
          </li>
          <li>
            <strong>GUID on flat referrals</strong> — Tableau&apos;s
            <code> COUNTD(GUID) </code>needs a per-referral GUID; we only cache
            <code> referral_id</code> / <code>referral_number</code>.
          </li>
          <li>
            <strong>Denormalized Parent1/2/3 on facilities (nice-to-have)</strong> —
            we walk <code>parentId</code> today, but a flat field would make
            reporting joins simpler downstream.
          </li>
        </ul>
      </details>

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={hierarchy}
          availableFacilityIds={dataFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => {
            setSelectedFacilityIds(ids);
            setActiveCustomerId('');
            setActiveFilterId('');
            clearGlobalScope();
          }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}
    </div>
  );
}

export default Drilldown20;
